import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Star, Paperclip, Send, CheckCircle2, Undo2, FileText } from "lucide-react";

export const OUTCOMES = {
  done: { ar: "أُنجزت", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  partial: { ar: "أُنجزت جزئياً", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  blocked: { ar: "تعذّر الإنجاز (معوقات)", cls: "bg-red-100 text-red-700 border-red-200" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "medium", timeStyle: "short" }) : "");

export const Stars = ({ value = 0, onChange, size = "h-4 w-4", testid }) => (
  <div className="inline-flex items-center gap-0.5" dir="ltr" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={testid ? `${testid}-${n}` : undefined}
        className={onChange ? "transition-transform hover:scale-125" : "cursor-default"}>
        <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

const openFile = async (task, att, mode) => {
  try {
    const blob = await (mode === "portal" ? api.portalTaskReportFile : api.taskReportFile)(task.id, att.id);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch { toast.error("تعذّر فتح المرفق"); }
};

// Report + rating summary shown on the task card
export const TaskReportView = ({ task, mode = "admin" }) => {
  const rep = task.report || (task.reports || []).slice(-1)[0];
  const ret = task.status === "open" ? (task.returns || []).slice(-1)[0] : null;
  if (!rep && !ret && !task.review) return null;
  const o = rep && (OUTCOMES[rep.outcome] || OUTCOMES.done);
  return (
    <div className="mt-2 space-y-1.5" data-testid={`task-report-view-${task.id}`}>
      {ret && <p className="rounded-lg bg-orange-50 px-2.5 py-1.5 text-[11px] text-orange-800" data-testid={`task-return-note-${task.id}`}>
        <Undo2 className="me-1 inline h-3 w-3" />أُعيدت للاستكمال من {ret.by_name}: {ret.note}</p>}
      {rep && task.status !== "open" && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px]">
          <div className="flex flex-wrap items-center gap-1.5">
            <FileText className="h-3 w-3 text-slate-500" /><span className="font-bold text-slate-700">تقرير {rep.by_name}</span>
            <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${o.cls}`} data-testid={`task-report-outcome-${task.id}`}>{o.ar}</span>
            <span className="text-slate-400">{fmt(rep.at)}</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-slate-600" data-testid={`task-report-text-${task.id}`}>{rep.text}</p>
          {rep.attachment && <button type="button" onClick={() => openFile(task, rep.attachment, mode)} data-testid={`task-report-file-${task.id}`}
            className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:underline"><Paperclip className="h-3 w-3" />{rep.attachment.name}</button>}
        </div>)}
      {task.status === "done" && task.review && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`task-review-${task.id}`}>
          <Stars value={task.review.rating} size="h-3.5 w-3.5" /><span className="font-bold text-slate-700">{task.review.rating}/5</span>
          <span className="text-slate-400">اعتمدها {task.review.by_name}</span>
          {task.review.note && <span className="w-full text-slate-600">«{task.review.note}»</span>}
        </div>)}
    </div>
  );
};

// Assignee: submit completion report
export const TaskReportDialog = ({ task, mode = "admin", onClose, onDone }) => {
  const [outcome, setOutcome] = useState("done");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (task) { setOutcome("done"); setText(""); setFile(null); } }, [task]);
  const submit = async () => {
    if (text.trim().length < 3) { toast.error("اكتب تقرير ما تم إنجازه أو المعوقات"); return; }
    const fd = new FormData();
    fd.append("outcome", outcome); fd.append("text", text.trim());
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      await (mode === "portal" ? api.portalTaskReport : api.taskReport)(task.id, fd);
      toast.success("تم رفع التقرير — بانتظار اعتماد منشئ المهمة"); onClose(); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر رفع التقرير"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-md" data-testid="task-report-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading">رفع تقرير الإنجاز</DialogTitle>
          <DialogDescription>«{task?.title}» — لن تُغلق المهمة إلا بعد اعتماد منشئها</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-1.5">
            {Object.entries(OUTCOMES).map(([k, v]) => (
              <button key={k} type="button" onClick={() => setOutcome(k)} data-testid={`task-outcome-${k}`}
                className={`rounded-xl border px-3 py-2 text-right text-sm transition-colors ${outcome === k ? `${v.cls} font-bold` : "border-slate-200 hover:bg-slate-50"}`}>{v.ar}</button>
            ))}
          </div>
          <div>
            <Label className="text-xs">التقرير (إلزامي) — ما تم إنجازه أو المعوقات</Label>
            <Textarea data-testid="task-report-text" rows={4} value={text} onChange={(e) => setText(e.target.value)} className="mt-1" />
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50">
            <Paperclip className="h-4 w-4" />{file ? file.name : "إرفاق صورة أو ملف PDF (اختياري)"}
            <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="task-report-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button data-testid="task-report-submit" onClick={submit} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} رفع التقرير
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Creator: approve + rate, or return to assignee
export const TaskApproveDialog = ({ task, mode = "admin", onClose, onDone }) => {
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { if (task) { setRating(0); setNote(""); } }, [task]);
  const portal = mode === "portal";
  const approve = async () => {
    if (!rating) { toast.error("اختر التقييم بالنجوم"); return; }
    setBusy("ok");
    try { await (portal ? api.portalTaskApprove : api.taskApprove)(task.id, { rating, note: note.trim() }); toast.success("تم اعتماد المهمة وإغلاقها"); onClose(); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الاعتماد"); } finally { setBusy(""); }
  };
  const giveBack = async () => {
    if (!note.trim()) { toast.error("اكتب سبب الإعادة في خانة الملاحظة"); return; }
    setBusy("ret");
    try { await (portal ? api.portalTaskReturn : api.taskReturn)(task.id, note.trim()); toast.success("أُعيدت المهمة للموكَّل إليه"); onClose(); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإعادة"); } finally { setBusy(""); }
  };
  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-md" data-testid="task-approve-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading">اعتماد وإغلاق المهمة</DialogTitle>
          <DialogDescription>«{task?.title}»</DialogDescription>
        </DialogHeader>
        {task && (task.status === "review" ? <TaskReportView task={task} mode={mode} />
          : <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">لم يرفع الموكَّل إليه تقريراً بعد — يمكنك الإغلاق والتقييم مباشرة.</p>)}
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
            <span className="text-sm">تقييم الإنجاز</span><Stars value={rating} onChange={setRating} size="h-6 w-6" testid="task-rating" />
          </div>
          <div>
            <Label className="text-xs">ملاحظة (اختيارية للاعتماد — إلزامية عند الإعادة)</Label>
            <Textarea data-testid="task-approve-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {task?.status === "review" ? <Button variant="outline" data-testid="task-return-btn" onClick={giveBack} disabled={!!busy} className="gap-1.5 text-orange-700">
            {busy === "ret" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} إعادة للموكَّل إليه</Button> : <span />}
          <Button data-testid="task-approve-btn" onClick={approve} disabled={!!busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
            {busy === "ok" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} اعتماد وإغلاق
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
